import React from "react";
import {
  Page,
  EmptyState,
  SkeletonBodyText,
  Card,
  Button,
  InlineStack,
  Text,
  ButtonGroup,
  Avatar,
  Badge,
  BlockStack,
  Box,
  Divider,
  Bleed,
} from "@medentic/polaris-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { AppPageWrapper } from "@shared/ui";
import { PlayCircleIcon, QuestionCircleIcon } from "@medentic/polaris-icons";
import { QES } from "../types";
import dayjs from "dayjs";
import { User } from "@shared/types/user";

export const QESPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { GET, DELETE } = useApiRequest();
  const queryClient = useQueryClient();

  const user = queryClient.getQueryData<User>(["user"]);

  const locale = i18n.language;
  const userTimezone = user?.timezone?.id ?? "UTC";

  dayjs.locale(locale);

  const { data, isLoading } = useQuery({
    queryKey: ["userQES"],
    queryFn: () => GET<QES[]>(`/api/user/me/qes`),
    refetchOnMount: true,
    staleTime: 0,
  });

  const disconnectMutation = useMutation({
    mutationFn: (qesId: string) => DELETE(`/api/user/me/qes/${qesId}`),
    onSuccess: () => {
      // Refetch QES data after successful disconnect
      queryClient.invalidateQueries({ queryKey: ["userQES"] });
    },
  });

  const handleDisconnect = async (qesId: string) => {
    await disconnectMutation.mutateAsync(qesId);
  };

  const renderEmptySection = () => {
    return (
      <Card>
        <EmptyState
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          heading={t("qes.empty.heading")}
          action={{
            content: t("qes.empty.action"),
            url: t("qes.empty.actionUrl"),
            external: true,
          }}
          secondaryAction={{
            content: t("qes.empty.learnMore"),
            url: t("qes.empty.learnMoreUrl"),
            external: true,
          }}
        >
          <Text as="p">{t("qes.empty.description")}</Text>
        </EmptyState>
      </Card>
    );
  };

  const renderQESSection = () => {
    const personalQES = data?.filter((qes) => !qes.delegated) || [];
    const delegatedQES = data?.filter((qes) => qes.delegated) || [];

    return (
      <Card padding="0">
        <Box padding="400">
          <BlockStack gap="400">
            <InlineStack gap="200" align="space-between">
              <Text as="h2" variant="headingMd">
                {t("qes.personal.title")}
              </Text>
              <Button variant="plain">{t("qes.viewActivity")}</Button>
            </InlineStack>
            <Text as="p" tone="subdued">
              {t("qes.personal.description")}
            </Text>

            {personalQES.map((qes) => {
              const lastConnectedAt = dayjs.utc(qes.lastConnectedAt).fromNow();

              return (
                <Box
                  key={qes.id}
                  padding="400"
                  borderColor="border"
                  borderWidth="025"
                  borderStyle="solid"
                  borderRadius="400"
                >
                  <InlineStack
                    key={qes.id}
                    align="space-between"
                    blockAlign="center"
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <Avatar source={qes.issuer.logoSVG} />
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="p" fontWeight="bold">
                            {qes.displayName}
                          </Text>

                          {qes.primary && (
                            <Badge tone="info">{t("qes.primary")}</Badge>
                          )}
                          <Badge
                            tone={qes.isConnected ? "success" : "critical"}
                          >
                            {qes.isConnected
                              ? t("qes.online")
                              : t("qes.offline")}
                          </Badge>
                        </InlineStack>
                        <Text as="p" tone="subdued">
                          {qes.issuer.name}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t("qes.validTill", {
                            date: dayjs
                              .utc(qes.validTo)
                              .tz(userTimezone)
                              .format("LL"),
                          })}
                        </Text>
                        {!qes.isConnected && (
                          <Text as="p" tone="subdued">
                            {t("qes.lastOnline", { date: lastConnectedAt })}
                          </Text>
                        )}
                      </BlockStack>
                    </InlineStack>
                    <ButtonGroup>
                      {qes.actions.includes("primary") && (
                        <Button variant="plain">{t("qes.setPrimary")}</Button>
                      )}
                      {qes.actions.includes("disconnect") && (
                        <Button
                          variant="plain"
                          tone="critical"
                          onClick={() => handleDisconnect(qes.id)}
                          loading={disconnectMutation.isPending}
                        >
                          {t("qes.disconnect")}
                        </Button>
                      )}
                    </ButtonGroup>
                  </InlineStack>
                </Box>
              );
            })}
          </BlockStack>
        </Box>
        <Bleed marginInline="400">
          <Divider />
        </Bleed>
        <Box padding="400">
          <BlockStack gap="400">
            <InlineStack gap="200" align="space-between">
              <Text as="h2" variant="headingMd">
                {t("qes.authorized.title")}
              </Text>
              <Button variant="plain">{t("qes.authorized.action")}</Button>
            </InlineStack>

            <Text as="p" tone="subdued">
              {t("qes.authorized.description")}
            </Text>
            <br />
            <InlineStack gap="200" align="center">
              <Text tone="subdued" as="p">
                {t("qes.authorized.empty")}
              </Text>
            </InlineStack>
            <InlineStack gap="200" align="center">
              <Box>
                <Button disabled>{t("qes.authorized.action")}</Button>
              </Box>
            </InlineStack>
          </BlockStack>
        </Box>
        <Bleed marginInline="400">
          <Divider />
        </Bleed>

        <Box padding="400">
          <BlockStack gap="400">
            <InlineStack gap="200" align="space-between">
              <Text as="h2" variant="headingMd">
                {t("qes.delegated.title")}
              </Text>
              <Button variant="plain">{t("qes.viewDelegatedActivity")}</Button>
            </InlineStack>
            <Text as="p" tone="subdued">
              {t("qes.delegated.description")}
            </Text>
            {delegatedQES.length > 0 ? (
              delegatedQES.map((qes: QES) => (
                <Box
                  key={qes.id}
                  padding="400"
                  borderColor="border"
                  borderWidth="025"
                  borderStyle="solid"
                  borderRadius="400"
                >
                  <InlineStack
                    key={qes.id}
                    align="space-between"
                    blockAlign="center"
                  >
                    <InlineStack gap="200" blockAlign="center">
                      <Avatar source={qes.issuer.logoSVG} />
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="p" fontWeight="bold">
                            {qes.displayName}
                          </Text>
                          {qes.primary && (
                            <Badge tone="info">{t("qes.primary")}</Badge>
                          )}
                          <Badge
                            tone={qes.isConnected ? "success" : "critical"}
                          >
                            {qes.isConnected
                              ? t("qes.online")
                              : t("qes.offline")}
                          </Badge>
                        </InlineStack>
                        <Text as="p" tone="subdued">
                          {t("qes.validTill", {
                            date: dayjs
                              .utc(qes.validTo)
                              .tz(userTimezone)
                              .format("LL"),
                          })}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                    <ButtonGroup>
                      {qes.actions.includes("primary") && (
                        <Button variant="plain">{t("qes.setPrimary")}</Button>
                      )}
                      {qes.actions.includes("disconnect") && (
                        <Button
                          variant="plain"
                          tone="critical"
                          onClick={() => handleDisconnect(qes.id)}
                          loading={disconnectMutation.isPending}
                        >
                          {t("qes.disconnect")}
                        </Button>
                      )}
                    </ButtonGroup>
                  </InlineStack>
                </Box>
              ))
            ) : (
              <>
                <br />
                <InlineStack gap="200" align="center">
                  <Text as="p" tone="subdued">
                    {t("qes.delegated.empty")}
                  </Text>
                </InlineStack>
                <br />
              </>
            )}
          </BlockStack>
        </Box>
      </Card>
    );
  };

  return (
    <AppPageWrapper>
      <Page
        narrowWidth
        title={t("qes.title")}
        additionalMetadata={
          <InlineStack gap="300">
            <Button
              variant="plain"
              icon={QuestionCircleIcon}
              url={t("qes.empty.learnMoreUrl")}
              external
            >
              {t("qes.empty.learnMore")}
            </Button>
            <Button variant="plain" icon={PlayCircleIcon}>
              {t("qes.empty.watchVideo")}
            </Button>
          </InlineStack>
        }
      >
        {isLoading ? (
          <Card>
            <SkeletonBodyText lines={10} />
          </Card>
        ) : !data?.length ? (
          renderEmptySection()
        ) : (
          renderQESSection()
        )}
      </Page>
    </AppPageWrapper>
  );
};
